import {SketchEntityKind} from './SketchEntityKinds';
import {SketchRelationKind} from './SketchRelationKinds';
import type {NormalizedSelection, RelationRule, ValidationResult,} from './SketchRelationTypes';

/**
 * 线类实体集合。
 */
const LINE_KINDS: SketchEntityKind[] = [SketchEntityKind.Line, SketchEntityKind.CenterLine];

/**
 * 点类实体集合。
 */
const POINT_KINDS: SketchEntityKind[] = [SketchEntityKind.Point, SketchEntityKind.EndPoint];

/**
 * 平面类实体集合。
 */
const PLANE_LIKE_KINDS: SketchEntityKind[] = [SketchEntityKind.ReferencePlane, SketchEntityKind.PlanarFace];

/**
 * 可用于相切关系的曲线类型集合。
 */
const TANGENT_CURVE_KINDS: SketchEntityKind[] = [
    SketchEntityKind.Arc,
    SketchEntityKind.Circle,
    SketchEntityKind.Ellipse,
    SketchEntityKind.Spline,
];

/**
 * 曲线长度相等关系允许的无序实体组合键集合。
 */
const EQUAL_CURVE_LENGTH_PAIR_KEYS: Set<string> = new Set<string>([
    buildPairKey(SketchEntityKind.Circle, SketchEntityKind.Arc),
    buildPairKey(SketchEntityKind.Circle, SketchEntityKind.Circle),
    buildPairKey(SketchEntityKind.Arc, SketchEntityKind.Arc),
    
    buildPairKey(SketchEntityKind.Arc, SketchEntityKind.Line),
    
    buildPairKey(SketchEntityKind.Line, SketchEntityKind.Circle),
    buildPairKey(SketchEntityKind.Circle, SketchEntityKind.Spline),
    buildPairKey(SketchEntityKind.Spline, SketchEntityKind.Line),
]);

/**
 * 草图几何关系规则表。
 */
export const SKETCH_RELATION_RULES: RelationRule[] = [
    
    {
        kind: SketchRelationKind.Horizontal,
        minCount: 1,
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
            {
                roles: [
                    {kinds: POINT_KINDS, min: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 10,
        failureReason: '当前选择不满足该关系要求',
    },
    {
        kind: SketchRelationKind.Vertical,
        minCount: 1,
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
            {
                roles: [
                    {kinds: POINT_KINDS, min: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 11,
        failureReason: '当前选择不满足该关系要求',
    },
    
    {
        kind: SketchRelationKind.Collinear,
        minCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 20,
    },
    {
        kind: SketchRelationKind.Coradial,
        minCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: [SketchEntityKind.Arc], min: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 30,
    },
    {
        kind: SketchRelationKind.Perpendicular,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 2, max: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 40,
    },
    {
        kind: SketchRelationKind.Parallel,
        minCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 50,
    },
    {
        kind: SketchRelationKind.Parallel,
        minCount: 2,
        maxCount: 2,
        sketchModes: ['3D'],
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 1, max: 1},
                    {kinds: PLANE_LIKE_KINDS, min: 1, max: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 52,
    },
    {
        kind: SketchRelationKind.ParallelToYZ,
        minCount: 2,
        maxCount: 2,
        sketchModes: ['3D'],
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 1, max: 1},
                    {kinds: PLANE_LIKE_KINDS, min: 1, max: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 53,
        failureReason: '当前选择不满足该关系要求',
    },
    {
        kind: SketchRelationKind.ParallelToZX,
        minCount: 2,
        maxCount: 2,
        sketchModes: ['3D'],
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 1, max: 1},
                    {kinds: PLANE_LIKE_KINDS, min: 1, max: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 54,
        failureReason: '当前选择不满足该关系要求',
    },
    {
        kind: SketchRelationKind.AlongZ,
        minCount: 2,
        maxCount: 2,
        sketchModes: ['3D'],
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 1, max: 1},
                    {kinds: PLANE_LIKE_KINDS, min: 1, max: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 55,
        failureReason: '当前选择不满足该关系要求',
    },
    {
        kind: SketchRelationKind.Tangent,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: TANGENT_CURVE_KINDS, min: 1, max: 1},
                    {kinds: LINE_KINDS, min: 1, max: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
            {
                roles: [
                    {kinds: TANGENT_CURVE_KINDS, min: 1, max: 1},
                    {kinds: [SketchEntityKind.Arc, SketchEntityKind.Circle, SketchEntityKind.Ellipse], min: 1, max: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 60,
    },
    {
        kind: SketchRelationKind.Concentric,
        minCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: [SketchEntityKind.Arc, SketchEntityKind.Circle], min: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
            {
                roles: [
                    {kinds: POINT_KINDS, min: 1, max: 1},
                    {kinds: [SketchEntityKind.Arc, SketchEntityKind.Circle], min: 1, max: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 70,
    },
    {
        kind: SketchRelationKind.Midpoint,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: POINT_KINDS, min: 1, max: 1},
                    {kinds: LINE_KINDS, min: 1, max: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 80,
    },
    {
        kind: SketchRelationKind.Intersection,
        minCount: 3,
        maxCount: 3,
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 2, max: 2},
                    {kinds: POINT_KINDS, min: 1, max: 1},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 90,
    },
    {
        kind: SketchRelationKind.Coincident,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: POINT_KINDS, min: 1, max: 1},
                    {
                        kinds: [
                            SketchEntityKind.Line,
                            SketchEntityKind.CenterLine,
                            SketchEntityKind.Arc,
                            SketchEntityKind.Circle,
                            SketchEntityKind.Ellipse,
                            SketchEntityKind.Parabola,
                            SketchEntityKind.Spline,
                        ],
                        min: 1,
                        max: 1,
                    },
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 100,
    },
    {
        kind: SketchRelationKind.Equal,
        minCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: LINE_KINDS, min: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
            {
                roles: [
                    {kinds: [SketchEntityKind.Arc], min: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
            {
                roles: [
                    {kinds: [SketchEntityKind.Circle], min: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 110,
    },
    {
        kind: SketchRelationKind.EqualCurvature,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: [SketchEntityKind.Spline], min: 2, max: 2},
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 120,
    },
    {
        kind: SketchRelationKind.EqualCurveLength,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {
                        kinds: [
                            SketchEntityKind.Line,
                            SketchEntityKind.Arc,
                            SketchEntityKind.Circle,
                            SketchEntityKind.Spline,
                        ],
                        min: 2,
                        max: 2,
                    },
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        validator: (selection: NormalizedSelection[]): ValidationResult => {
            const pairKey = buildPairKey(selection[0].kind, selection[1].kind);
            if (EQUAL_CURVE_LENGTH_PAIR_KEYS.has(pairKey)) {
                return {ok: true};
            }
            return {
                ok: false,
                reason: '当前选择不满足该关系要求',
            };
        },
        priority: 130,
    },
    {
        kind: SketchRelationKind.Symmetric,
        minCount: 3,
        maxCount: 3,
        signatures: [
            {
                roles: [
                    {kinds: [SketchEntityKind.CenterLine], min: 1, max: 1},
                    {
                        kinds: [
                            SketchEntityKind.Point,
                            SketchEntityKind.EndPoint,
                            SketchEntityKind.Line,
                            SketchEntityKind.CenterLine,
                            SketchEntityKind.Arc,
                            SketchEntityKind.Circle,
                            SketchEntityKind.Ellipse,
                            SketchEntityKind.Parabola,
                            SketchEntityKind.Spline,
                            SketchEntityKind.Slot,
                        ],
                        min: 2,
                        max: 2,
                    },
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        validator: (selection: NormalizedSelection[]): ValidationResult => {
            const centerlines = selection.filter((item) => item.isCenterLine);
            const axis = centerlines.length === 1
                ? centerlines[0]
                : selection.at(-1)?.isCenterLine
                    ? selection.at(-1)
                    : null;
            const peers = selection.filter((item) => item !== axis);
            if (!axis || peers.length !== 2) {
                return {ok: false, reason: '当前选择不满足该关系要求'};
            }
            if (peers[0].baseKind !== peers[1].baseKind) {
                return {ok: false, reason: '当前选择不满足该关系要求'};
            }
            return {ok: true};
        },
        priority: 140,
    },
    {
        kind: SketchRelationKind.Fix,
        minCount: 1,
        signatures: [
            {
                roles: [{predicate: () => true, min: 1}],
            },
        ],
        priority: 150,
    },
    {
        kind: SketchRelationKind.Radius,
        minCount: 1,
        maxCount: 1,
        signatures: [
            {
                roles: [{kinds: [SketchEntityKind.Arc], min: 1, max: 1}],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 151,
        failureReason: '当前选择不满足该关系要求',
    },
    {
        kind: SketchRelationKind.FixSlot,
        minCount: 1,
        signatures: [
            {
                roles: [{kinds: [SketchEntityKind.Slot], min: 1}],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 160,
    },
    {
        kind: SketchRelationKind.Pierce,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: POINT_KINDS, min: 1, max: 1},
                    {
                        kinds: [
                            SketchEntityKind.ReferenceAxis,
                            SketchEntityKind.ModelEdge,
                            SketchEntityKind.Line,
                            SketchEntityKind.CenterLine,
                            SketchEntityKind.Spline,
                        ],
                        min: 1,
                        max: 1,
                    },
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 170,
    },
    {
        kind: SketchRelationKind.MergePoints,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [{kinds: POINT_KINDS, min: 2, max: 2}],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 180,
    },
    {
        kind: SketchRelationKind.TwiceDistance,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: [SketchEntityKind.CenterLine], min: 1, max: 1},
                    {
                        predicate: (item) => (
                            !item.isPlaneLike
                            && !item.isAxisLike
                            && !item.isEdgeLike
                        ),
                        min: 1,
                        max: 1,
                    },
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 190,
    },
    {
        kind: SketchRelationKind.EqualSlot,
        minCount: 2,
        signatures: [
            {
                roles: [{kinds: [SketchEntityKind.Slot], min: 2}],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 200,
    },
    {
        kind: SketchRelationKind.OnEdge,
        minCount: 1,
        signatures: [
            {
                roles: [{kinds: [SketchEntityKind.ModelEdge], min: 1}],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 210,
    },
    {
        kind: SketchRelationKind.OnPlane,
        minCount: 1,
        signatures: [
            {
                roles: [{kinds: PLANE_LIKE_KINDS, min: 1}],
                reason: '当前选择不满足该关系要求',
            },
        ],
        validator: (_selection: NormalizedSelection[], ctx): ValidationResult => {
            if (!ctx?.hasReferencePlaneContext) {
                return {
                    ok: false,
                    reason: '当前选择不满足该关系要求',
                };
            }
            return {ok: true};
        },
        priority: 220,
    },
    {
        kind: SketchRelationKind.OnSurface,
        minCount: 1,
        signatures: [
            {
                roles: [{kinds: [SketchEntityKind.SurfaceFace], min: 1}],
                reason: '当前选择不满足该关系要求',
            },
        ],
        validator: (_selection: NormalizedSelection[], ctx): ValidationResult => {
            if (!ctx?.hasReferencePlaneContext) {
                return {
                    ok: false,
                    reason: '当前选择不满足该关系要求',
                };
            }
            return {ok: true};
        },
        priority: 230,
    },
    {
        kind: SketchRelationKind.TangentToFace,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {
                        kinds: [
                            SketchEntityKind.Point,
                            SketchEntityKind.EndPoint,
                            SketchEntityKind.Line,
                            SketchEntityKind.CenterLine,
                            SketchEntityKind.Arc,
                            SketchEntityKind.Circle,
                            SketchEntityKind.Ellipse,
                            SketchEntityKind.Spline,
                            SketchEntityKind.Slot,
                        ],
                        min: 1,
                        max: 1,
                    },
                    {
                        kinds: [SketchEntityKind.PlanarFace, SketchEntityKind.SurfaceFace],
                        min: 1,
                        max: 1,
                    },
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 240,
    },
    {
        kind: SketchRelationKind.TorsionContinuity,
        minCount: 2,
        maxCount: 2,
        signatures: [
            {
                roles: [
                    {kinds: [SketchEntityKind.Spline], min: 1, max: 1},
                    {
                        kinds: [
                            SketchEntityKind.Spline,
                            SketchEntityKind.Arc,
                            SketchEntityKind.Ellipse,
                            SketchEntityKind.ModelEdge,
                        ],
                        min: 1,
                        max: 1,
                    },
                ],
                reason: '当前选择不满足该关系要求',
            },
        ],
        priority: 250,
    },
];

/**
 * 构建无序实体对键。
 * @param left 左侧实体类型。
 * @param right 右侧实体类型。
 * @returns 无序组合键。
 */
function buildPairKey(left: SketchEntityKind, right: SketchEntityKind): string {
    const ordered = [left, right].sort();
    return `${ordered[0]}::${ordered[1]}`;
}


